import { useCallback, useState } from "react";
import { useTranslation } from "../../i18n";
import { Button } from "../Button/Button";
import { Modal, ModalFooter } from "../Modal";
import { ConfirmDialogProps } from "./types";

export const ConfirmDialog = (props: ConfirmDialogProps) => {
  const { t } = useTranslation();
  const {
    variant,
    submitAction,
    submitText,
    onResolve,
    onError,
    cancelButtonProps,
    submitButtonProps,
    description,
    ...restModalProps
  } = props;
  const [loading, setLoading] = useState<boolean>(false);

  const handleDismiss = useCallback(() => {
    restModalProps.onOpenChange?.(false);
  }, [restModalProps.onOpenChange]);

  const handleSubmit = useCallback(() => {
    const result = submitAction();

    if (result && typeof result.then === "function") {
      setLoading(true);
      result
        .then(() => {
          onResolve ? onResolve() : handleDismiss();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      onResolve ? onResolve() : handleDismiss();
    }
  }, [submitAction, onResolve, handleDismiss, onError]);

  return (
    <Modal
      closeOnOutsidePress={false}
      showClose={false}
      {...restModalProps}
      headerClassName="border-none"
    >
      {description && (
        <div className="px-4 pb-4 overflow-y-auto max-h-[60vh] whitespace-pre-line text-sm text-neutral-500/50">
          {description}
        </div>
      )}
      <ModalFooter className="pt-2 border-none">
        <Button
          variant="outlined"
          color="neutral"
          disabled={loading}
          {...cancelButtonProps}
          onClick={handleDismiss}
          data-e2e-id={`${variant || "default"}Dialog__button__cancel`}
        >
          {cancelButtonProps?.children ?? t("confirmDialog.cancel")}
        </Button>
        <Button
          color={variant === "danger" ? "destructive" : "primary"}
          {...submitButtonProps}
          onClick={handleSubmit}
          loading={loading}
          data-e2e-id={`${variant || "default"}Dialog__button__approve`}
        >
          {submitButtonProps?.children ?? submitText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
